import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography
} from "@mui/material";
import firebase from "firebase";
import { auth, firestore } from "../services/firebase";

export default function SignupCard({
    title,
    description,
    buttonLabel = "Continue",
    onSuccess,
    afterAuthRoute
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [credentials, setCredentials] = useState({
        email: "",
        password: "",
        confirmPassword: ""
    });

    const disableSubmit = useMemo(() => {
        return (
            !credentials.email ||
            !credentials.password ||
            !credentials.confirmPassword
        );
    }, [credentials]);

    const handleCredentialChange = (event) => {
        const { name, value } = event.target;
        setCredentials((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const ensureUserDocument = useCallback(async (user) => {
        const email = user.email?.toLowerCase() || "";
        const usersCollection = firestore.collection("Users");
        const uidRef = usersCollection.doc(user.uid);
        const uidSnapshot = await uidRef.get();

        let targetRef = uidRef;
        let existingData = uidSnapshot.exists ? uidSnapshot.data() : null;

        if (!uidSnapshot.exists) {
            const emailMatch = await usersCollection
                .where("email", "==", email)
                .limit(1)
                .get();
            if (!emailMatch.empty) {
                targetRef = emailMatch.docs[0].ref;
                existingData = emailMatch.docs[0].data();
            }
        }

        const basePayload = {
            email,
            has_updated_categories: false,
            last_event_signup_at: firebase.firestore.FieldValue.serverTimestamp()
        };

        if (!existingData || existingData?.unassigned_to_bar === undefined) {
            basePayload.unassigned_to_bar = true;
        }

        if (!existingData || existingData?.onBoardingComplete === undefined) {
            basePayload.onBoardingComplete = false;
        }

        if (existingData) {
            await targetRef.set(basePayload, { merge: true });
            return existingData;
        }

        await targetRef.set(
            {
                roles: {},
                isAdmin: false,
                visibility: true,
                token: "",
                onBoardingComplete: false,
                ...basePayload
            },
            { merge: true }
        );
        return null;
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (credentials.password !== credentials.confirmPassword) {
            setError("Passwords must match.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            let userCredential;
            try {
                userCredential = await auth.createUserWithEmailAndPassword(
                    credentials.email.trim(),
                    credentials.password
                );
            } catch (createError) {
                if (createError.code === "auth/email-already-in-use") {
                    userCredential = await auth.signInWithEmailAndPassword(
                        credentials.email.trim(),
                        credentials.password
                    );
                } else {
                    throw createError;
                }
            }

            const { user } = userCredential;
            const existingData = await ensureUserDocument(user);
            if (onSuccess) {
                await onSuccess(user, existingData);
            }
            if (afterAuthRoute) {
                window.location.assign(afterAuthRoute);
            }
        } catch (submitError) {
            setError(
                submitError?.message || "Unable to continue. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <OuterContainer>
            <Card>
                <HeaderLogo src="/logo2.png" alt="Bar Hopper" />
                <Typography component="h1" variant="h4" gutterBottom align="center">
                    {title}
                </Typography>
                {description && (
                    <Typography variant="body1" paragraph align="center">
                        {description}
                    </Typography>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <form onSubmit={handleSubmit}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Email"
                            name="email"
                            type="email"
                            value={credentials.email}
                            onChange={handleCredentialChange}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Password"
                            name="password"
                            type="password"
                            value={credentials.password}
                            onChange={handleCredentialChange}
                            required
                            fullWidth
                            inputProps={{ minLength: 6 }}
                        />
                        <TextField
                            label="Confirm Password"
                            name="confirmPassword"
                            type="password"
                            value={credentials.confirmPassword}
                            onChange={handleCredentialChange}
                            required
                            fullWidth
                            inputProps={{ minLength: 6 }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={disableSubmit || loading}
                            endIcon={loading ? <CircularProgress size={18} /> : null}
                        >
                            {buttonLabel}
                        </Button>
                    </Box>
                </form>
            </Card>
        </OuterContainer>
    );
}

const OuterContainer = styled.div`
    position: relative;
    min-height: 100dvh;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 24px 16px;
    background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(196, 45, 62, 1) 100%
    );
    overflow-y: auto;
`;

const Card = styled.div`
    position: relative;
    z-index: 1;
    max-width: 420px;
    width: 100%;
    background: #f0f0f0;
    border-radius: 4px;
    padding: 32px;
    box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;

    form {
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 28px 24px;
    }
`;

const HeaderLogo = styled.img`
    width: 200px;
    height: auto;
    margin: 0 auto 8px;
    pointer-events: none;
    user-select: none;

    @media (max-width: 480px) {
        width: 140px;
    }
`;
